import { DeviceErrors, StreamStates, avSelectors, useAvContext } from "@minervaproject/avsubsystem";
import {
  IconButton,
  MicMutedIcon,
  MicUnmutedIcon,
  PinIcon,
  RefreshUserIcon,
  RefreshVideoIcon,
  StarIcon,
  UnpinIcon,
} from "@minervaproject/fds";
import { unwrapResult } from "@reduxjs/toolkit";
import { useState } from "react";

import { AvAlert } from "components/av/av_alert";
import { AvIssueIcon } from "components/av/av_issue_icon";
import { ParticipantAv } from "components/av/participant_av";
import { SignalsAndReactions } from "components/shared/signals_and_reactions";
import { createNoClassroomChangeToast } from "components/shared/toasts";
import { useCapability } from "hooks/capabilities";
import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import { PROFILE_PROPERTIES, useUserProperties } from "hooks/use_user_properties";
import { replaceUserOnStage, setStagePaneIsPinned } from "shared/reducers/classroom";
import { CapabilityType } from "shared/types";
import { classroomSelectors, localSelectors } from "store";
import { selectEffectivePttMode } from "store/selectors/classroom";
import { shortDisplayName } from "utils/user";

type UserPaneProps = {
  userId: string;
  currentUserCanControlClassroom: boolean;
  isSmallPane: boolean;
  isPinned: boolean;
  index: number;
};

function UserPane({
  userId,
  currentUserCanControlClassroom,
  isSmallPane,
  isPinned,
  index,
}: UserPaneProps) {
  const dispatch = useAppDispatch();
  const user = useUserProperties(userId, PROFILE_PROPERTIES.concat(["is_present", "is_speaking"]));

  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());
  const canControlClassroom = useCapability(CapabilityType.CAN_CONTROL_CLASSROOM, { userId });
  const isSelf = currentUserId === userId;

  const pttMode = useAppSelector(selectEffectivePttMode(userId));

  const mutedAudio = pttMode && user?.is_speaking === false;
  const cameraError = useAppSelector(classroomSelectors.selectUserCameraError(userId));
  const micError = useAppSelector(classroomSelectors.selectUserMicError(userId));
  const avAvailable = useAppSelector(classroomSelectors.selectUserHasAvailableAv(userId));
  const avIssue = !!micError || !avAvailable;
  const poorConnection = useAppSelector(classroomSelectors.selectUserHasPoorConnection(userId));

  const [showButtons, setShowButtons] = useState(false);
  const avConnection = useAvContext();

  const streamState = avSelectors.useStreamState(userId);
  const canReload =
    streamState && [StreamStates.playable, StreamStates.failed].includes(streamState.state);

  const handleClickPin = () => {
    dispatch(setStagePaneIsPinned({ index, isPinned: !isPinned }));
  };

  const handleReloadStream = () => {
    avConnection?.reloadStream(userId);
  };

  const handleReplaceUser = async () => {
    const actionResult = await dispatch(replaceUserOnStage({ userId }));
    const result = unwrapResult(actionResult);
    if (!result.updated) {
      createNoClassroomChangeToast(result.msg);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div
      className="w-full relative"
      onMouseEnter={() => setShowButtons(true)}
      onMouseLeave={() => setShowButtons(false)}
      data-testid={`user-pane-${userId}`}
    >
      <ParticipantAv userId={userId} isSelf={isSelf} includeAudio={false} />
      <SignalsAndReactions userId={userId} size={isSmallPane ? "lg" : "xl"} />
      <div
        className="absolute top-0 left-0 right-0 flex justify-end p1 bg-gradient-to-top"
        style={{
          opacity: showButtons ? 1 : 0,
          transition: "opacity 200ms linear",
        }}
        data-testid="user-pane-buttons"
      >
        <div className="flex items-center mr2">
          {currentUserCanControlClassroom && (
            <IconButton onClick={handleClickPin} aria-label={isPinned ? "Unpin user" : "Pin user"}>
              {isPinned ? (
                <UnpinIcon width={16} height={16} color="white" />
              ) : (
                <PinIcon width={16} height={16} color="white" />
              )}
              <span className="body-s text-white ml1" style={{ paddingBottom: "2px" }}>
                {isPinned ? "Pinned" : "Unpinned"}
              </span>
            </IconButton>
          )}
          {canReload && (
            <IconButton onClick={handleReloadStream} aria-label="Reload">
              <RefreshVideoIcon width={16} color="white" />
              <span className="body-s text-white ml1" style={{ paddingBottom: "2px" }}>
                Reload
              </span>
            </IconButton>
          )}
          {currentUserCanControlClassroom && (
            <IconButton
              onClick={handleReplaceUser}
              aria-label="Replace user"
              data-testid={`replace-user-${userId}`}
            >
              <RefreshUserIcon width={16} color="white" />
              <span className="body-s text-white ml1" style={{ paddingBottom: "2px" }}>
                Replace
              </span>
            </IconButton>
          )}
        </div>
      </div>
      <div
        className="absolute bottom-0 left-0 right-0 flex flex-column"
        style={{ opacity: user.is_present ? 1 : 0.5 }}
      >
        {!avAvailable && !micError ? (
          <AvAlert message="No Audio Signal" />
        ) : micError === DeviceErrors.AUDIO_NOT_ALLOWED ? (
          <AvAlert message="Mic Blocked" />
        ) : micError === DeviceErrors.AUDIO_NOT_FOUND ? (
          <AvAlert message="No Mic Detected" />
        ) : null}
        <div className="flex items-center justify-center p2 bg-gradient-to-bottom">
          {!showButtons ? null : mutedAudio ? (
            <span
              className="bg-red-shade-20 circle flex items-center justify-center mr1"
              style={{
                width: "24px",
                height: "24px",
              }}
            >
              <MicMutedIcon width={16} height={16} color="white" />
            </span>
          ) : (
            <MicUnmutedIcon width={20} height={20} color="white" className="mr1" />
          )}
          {canControlClassroom ? <StarIcon width={16} height={16} color="white" /> : null}
          <span className="mx1 text-white truncate">{shortDisplayName(user)}</span>
          {!avIssue && poorConnection ? (
            <AvIssueIcon
              poorConnection
              title="This user has a poor connection."
              style={{ marginLeft: "4px" }}
            />
          ) : !avIssue && cameraError ? (
            <AvIssueIcon
              title="This user has an issue with their camera."
              style={{ marginLeft: "4px" }}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

export { UserPane };
